"use client"

/**
 * 03 — Sound familiar? as a gallery of large cards (Eugene, 23.09, after
 * Apple's Watch gallery): one lifestyle photo per worry, the worry and what
 * we look at as one paragraph over its top left corner, the next cards
 * peeking in at the sides. The dots and the play button sit at the bottom
 * of the centred card. No tiles on the photo any more. On phones the text
 * sits at the bottom on frost (Eugene, 06.10), and there are no controls
 * and no autoplay, only the swipe (bugbash 14.10).
 *
 * The track is a native scroll-snap row, so a swipe or a trackpad moves it
 * and the card nearest the middle is the active one. It moves on by itself
 * every 6 s while it is on screen; the active dot is a pill whose fill is
 * the timer (a CSS animation whose end scrolls to the next card, so pausing
 * is `animation-play-state`). Touching the row, a dot or a side card pauses
 * it, and the play button starts it again. With reduced motion it starts
 * paused.
 */

import {
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from "react"
import Image from "next/image"
import { blobMediaUrl } from "@/lib/blob-url"
import {
  FIGMA_BACKGROUND_DEFAULTS,
  figmaNoiseUrl,
} from "./home-v4-baseline-background"
import { cn } from "@/lib/utils"
import { useHomeV4Copy } from "./home-v4-copy"

const STEP_MS = 6000

/**
 * Phones: the booking flow's frosted ramp (`progressiveBlur.ts`, as on the
 * close section) run up from the bottom of the card, where the text sits
 * there (Eugene, 06.10). The booking's stops are already measured down a
 * card, so they apply as they are.
 */
/** The membership card's film grain, a touch lighter and neutral white so it
 *  does not tint the warm photos (Eugene, 13.10). */
const GRAIN = figmaNoiseUrl({
  ...FIGMA_BACKGROUND_DEFAULTS,
  noiseColor: "#FFFFFF",
})
const GRAIN_OPACITY = 0.1

const PHONE_BLUR_LAYERS = [
  { blur: 1, stops: [32, 39, 52, 59] },
  { blur: 2, stops: [39, 46, 59, 66] },
  { blur: 4, stops: [42, 52, 66, 73] },
  { blur: 8, stops: [46, 59, 73, 80] },
  { blur: 16, stops: [59, 73, 86, 93] },
  { blur: 32, stops: [73, 86] },
  { blur: 64, stops: [80, 100] },
].map(({ blur, stops }) => {
  // Squeezed into the lower half, so the photo above the text stays sharp
  // (Eugene, 06.10).
  const [a, b, c, d] = stops.map(
    (n) => `${(50 + ((n - 32) / 68) * 50).toFixed(1)}%`
  )
  const mask =
    stops.length === 4
      ? `linear-gradient(to bottom, transparent ${a}, #000 ${b}, #000 ${c}, transparent ${d})`
      : `linear-gradient(to bottom, transparent ${a}, #000 ${b})`
  return {
    backdropFilter: `blur(${blur}px)`,
    WebkitBackdropFilter: `blur(${blur}px)`,
    maskImage: mask,
    WebkitMaskImage: mask,
  }
})

const CSS = `
  @keyframes hv4-fam-fill{from{transform:scaleX(0)}to{transform:scaleX(1)}}
  .hv4-fam-fill{transform-origin:left;animation:hv4-fam-fill ${STEP_MS}ms linear forwards}
  .hv4-fam[data-paused] .hv4-fam-fill{animation-play-state:paused}
  .hv4-fam-track{scrollbar-width:none}
  .hv4-fam-track::-webkit-scrollbar{display:none}
  @media (pointer:fine){.hv4-fam-track{cursor:grab}}
  .hv4-fam-track[data-dragging]{cursor:grabbing;scroll-snap-type:none;scroll-behavior:auto;user-select:none}
  .hv4-fam-track[data-dragging] *{pointer-events:none}
`

const REDUCED = "(prefers-reduced-motion: reduce)"
function subscribeReduced(onChange: () => void) {
  const mq = window.matchMedia(REDUCED)
  mq.addEventListener("change", onChange)
  return () => mq.removeEventListener("change", onChange)
}
const useReducedMotion = () =>
  useSyncExternalStore(
    subscribeReduced,
    () => window.matchMedia(REDUCED).matches,
    () => false
  )

function PauseIcon() {
  return (
    <svg viewBox="0 0 16 16" className="size-4" aria-hidden>
      <rect x="3.5" y="2.5" width="3" height="11" rx="1" fill="currentColor" />
      <rect x="9.5" y="2.5" width="3" height="11" rx="1" fill="currentColor" />
    </svg>
  )
}

function PlayIcon() {
  return (
    <svg viewBox="0 0 16 16" className="size-4" aria-hidden>
      <path
        d="M4.5 2.9v10.2a.9.9 0 0 0 1.37.77l8.1-5.1a.9.9 0 0 0 0-1.54l-8.1-5.1A.9.9 0 0 0 4.5 2.9Z"
        fill="currentColor"
      />
    </svg>
  )
}

export function HomeV4Familiar() {
  const { FAMILIAR } = useHomeV4Copy()
  const rows = FAMILIAR.rows
  const reduced = useReducedMotion()
  const [active, setActive] = useState(0)
  // "auto" plays unless reduced motion is on; the button sets the other two.
  const [mode, setMode] = useState<"auto" | "paused" | "playing">("auto")
  const playing = mode === "playing" || (mode === "auto" && !reduced)
  const [inView, setInView] = useState(false)
  const sectionRef = useRef<HTMLElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const cardRefs = useRef<(HTMLDivElement | null)[]>([])
  // While the row scrolls to a card we sent it to, the cards it passes on
  // the way do not count as active.
  const targetRef = useRef<number | null>(null)
  const frameRef = useRef(0)

  // Only count down while the gallery is on screen.
  useEffect(() => {
    const el = sectionRef.current
    if (!el) return
    const io = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.4 }
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])

  useEffect(() => () => cancelAnimationFrame(frameRef.current), [])

  const goTo = (i: number) => {
    const track = trackRef.current
    const card = cardRefs.current[i]
    if (!track || !card) return
    targetRef.current = i
    setActive(i)
    track.scrollTo({
      left: card.offsetLeft - (track.clientWidth - card.offsetWidth) / 2,
      behavior: reduced ? "auto" : "smooth",
    })
  }

  // The card whose centre is nearest the row's centre is the active one.
  const onScroll = () => {
    cancelAnimationFrame(frameRef.current)
    frameRef.current = requestAnimationFrame(() => {
      const track = trackRef.current
      if (!track) return
      const mid = track.scrollLeft + track.clientWidth / 2
      let nearest = 0
      let best = Infinity
      cardRefs.current.forEach((card, i) => {
        if (!card) return
        const d = Math.abs(card.offsetLeft + card.offsetWidth / 2 - mid)
        if (d < best) {
          best = d
          nearest = i
        }
      })
      if (targetRef.current !== null) {
        if (nearest !== targetRef.current) return
        targetRef.current = null
      }
      setActive(nearest)
    })
  }

  // Any hand on the row takes over from the timer.
  const takeOver = () => {
    targetRef.current = null
    setMode("paused")
  }

  const pick = (i: number) => {
    setMode("paused")
    goTo(i)
  }

  // Mouse drag (Eugene, 13.10): the row follows the pointer with snapping
  // off, then lands on a card. A drag past 60px goes one card that way, a
  // shorter one back to the nearest. Touch keeps the browser's own swipe.
  const drag = useRef<{
    x: number
    left: number
    from: number
    id: number
    moved: boolean
  } | null>(null)
  const justDragged = useRef(false)

  const onDragStart = (e: ReactPointerEvent<HTMLDivElement>) => {
    takeOver()
    if (e.pointerType !== "mouse" || e.button !== 0 || !trackRef.current) return
    drag.current = {
      x: e.clientX,
      left: trackRef.current.scrollLeft,
      from: active,
      id: e.pointerId,
      moved: false,
    }
  }

  const onDragMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const d = drag.current
    const track = trackRef.current
    if (!d || !track) return
    const dx = e.clientX - d.x
    if (!d.moved) {
      if (Math.abs(dx) < 5) return
      d.moved = true
      track.setPointerCapture(d.id)
      track.dataset.dragging = ""
    }
    track.scrollLeft = d.left - dx
  }

  const onDragEnd = (e: ReactPointerEvent<HTMLDivElement>) => {
    const d = drag.current
    const track = trackRef.current
    drag.current = null
    if (!d?.moved || !track) return
    delete track.dataset.dragging
    justDragged.current = true
    const dx = e.clientX - d.x
    const step = dx < -60 ? 1 : dx > 60 ? -1 : 0
    goTo(Math.min(rows.length - 1, Math.max(0, d.from + step)))
  }

  return (
    <section
      ref={sectionRef}
      aria-roledescription="carousel"
      aria-labelledby="hv4-fam-heading"
      data-paused={inView ? undefined : ""}
      className="hv4-fam bg-background pt-10 md:pt-[112px]"
    >
      <style>{CSS}</style>

      {/* Heading centred over the gallery (Eugene, 23.09). */}
      <div className="mx-auto max-w-[1440px] px-4 text-center md:px-10">
        <p className="type-label-lg text-neutral-700">{FAMILIAR.eyebrow}</p>
        <h2
          id="hv4-fam-heading"
          className="type-display-md-emphasized mt-[9px] text-neutral-900"
        >
          {FAMILIAR.heading}
        </h2>
      </div>

      {/* The card is as wide as the frame allows, up to 1280; the row's side
        padding is what is left over, so the first and last cards can sit in
        the middle too and their neighbours peek in at the sides. */}
      <div
        className="relative mt-6 md:mt-12"
        style={
          {
            "--card": "min(1280px, calc(100vw - 40px))",
          } as CSSProperties
        }
      >
        <div
          ref={trackRef}
          onScroll={onScroll}
          onPointerDown={onDragStart}
          onPointerMove={onDragMove}
          onPointerUp={onDragEnd}
          onPointerCancel={onDragEnd}
          onClickCapture={(e) => {
            // The click that ends a drag is not a pick.
            if (justDragged.current) {
              justDragged.current = false
              e.stopPropagation()
              e.preventDefault()
            }
          }}
          onWheel={(e) => {
            if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) takeOver()
          }}
          className="hv4-fam-track flex snap-x snap-mandatory gap-3 overflow-x-auto px-[calc((100vw-var(--card))/2)] md:gap-5 md:[--card:min(1280px,calc(100vw-160px))]"
        >
          {rows.map((row, i) => {
            const on = i === active
            return (
              <div
                key={row.title}
                ref={(el) => {
                  cardRefs.current[i] = el
                }}
                role="group"
                aria-roledescription="slide"
                aria-label={FAMILIAR.slide(i + 1, rows.length)}
                onClick={on ? undefined : () => pick(i)}
                className={cn(
                  "relative isolate flex h-[560px] w-[var(--card)] shrink-0 snap-center flex-col justify-end overflow-hidden rounded-[28px] bg-neutral-900 md:block md:aspect-[16/9] md:h-auto",
                  !on && "cursor-pointer"
                )}
              >
                <Image
                  src={
                    "local" in row && row.local
                      ? row.image
                      : blobMediaUrl(row.image)
                  }
                  alt=""
                  draggable={false}
                  fill
                  // A phone's card is tall and crops the 16:9 photo at the sides, so it
                  // needs the photo at the card's height, not the screen's width.
                  sizes="(min-width: 1440px) 1280px, (min-width: 768px) 100vw, 1000px"
                  className="-z-10 object-cover"
                  style={{ objectPosition: row.position }}
                />
                {/* A very light frosted patch under the text (Eugene, 24.09:
                  Apple-style blur, barely there). The blur fades out from the
                  top left corner, so it has no edge. */}
                <div
                  aria-hidden
                  className="absolute top-0 left-0 -z-10 hidden h-[75%] w-[70%] [mask-image:radial-gradient(ellipse_65%_75%_at_0%_0%,black_45%,transparent_100%)] backdrop-blur-[10px] md:block"
                />
                {/* Phones: frost and a dark scrim rising from the bottom,
                  under the text there. */}
                <div aria-hidden className="absolute inset-0 -z-10 md:hidden">
                  {PHONE_BLUR_LAYERS.map((style, j) => (
                    <div key={j} className="absolute inset-0" style={style} />
                  ))}
                </div>
                <div
                  aria-hidden
                  className="absolute inset-x-0 bottom-0 -z-10 h-[60%] bg-gradient-to-t from-black/70 via-black/30 to-transparent md:hidden"
                />
                {/* Desktop: darker in the top left corner, under the text,
                  and along the bottom, under the controls. */}
                <div
                  aria-hidden
                  className="absolute inset-0 -z-10 hidden bg-gradient-to-br from-black/60 via-black/15 to-transparent md:block"
                />
                <div
                  aria-hidden
                  className="absolute inset-x-0 bottom-0 -z-10 hidden h-1/3 bg-gradient-to-t from-black/40 to-transparent md:block"
                />
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-0 -z-10"
                  style={{
                    backgroundImage: GRAIN,
                    backgroundSize: "256px 256px",
                    opacity: GRAIN_OPACITY,
                  }}
                />
                {/* Bigger, and the title pure white against a softer body, so
                  the title reads as the heading (Eugene, 01.10). */}
                <p className="type-headline-md-emphasized md:type-headline-lg-emphasized max-w-[640px] p-6 md:max-w-[700px] md:p-14">
                  <span className="text-white">{row.title}</span>{" "}
                  <span className="text-white/75 md:text-white/60">
                    {row.body}
                  </span>
                </p>
              </div>
            )
          })}
        </div>

        {/* Controls: on the bottom of whichever card is in the middle, from
          md only. A phone swipes the cards and nothing turns them by itself:
          the dots and play under the gallery went unused and sat under the
          chat bubble (Vega and Eugene, bugbash 14.10). The timer is the
          dot's fill animation, so hiding the dots also stops the autoplay. */}
        <div className="hidden justify-center gap-2 md:pointer-events-none md:absolute md:inset-x-0 md:bottom-8 md:mt-0 md:flex">
          <div className="flex items-center rounded-full bg-neutral-200/70 px-2 md:pointer-events-auto md:bg-black/35 md:backdrop-blur-md">
            {rows.map((row, i) => {
              const on = i === active
              return (
                <button
                  key={row.title}
                  type="button"
                  aria-label={row.title}
                  aria-current={on ? "true" : undefined}
                  onClick={() => pick(i)}
                  className="flex h-10 items-center px-2 focus-visible:outline-none [&:focus-visible>span]:ring-2 [&:focus-visible>span]:ring-neutral-900 md:[&:focus-visible>span]:ring-white"
                >
                  {/* 10px dots, the active one a pill whose fill is the
                    timer (Eugene, 23.09: the first dots, 2px larger; the
                    photo thumbnails did not stay). */}
                  <span
                    className={cn(
                      "relative block h-2.5 overflow-hidden rounded-full transition-[width,background-color] duration-300",
                      on
                        ? "w-[34px] bg-neutral-300 md:bg-white/35"
                        : "w-2.5 bg-neutral-400 hover:bg-neutral-500 md:bg-white/50 md:hover:bg-white/80"
                    )}
                  >
                    {on ? (
                      playing ? (
                        <span
                          key={active}
                          className="hv4-fam-fill absolute inset-0 bg-neutral-900 md:bg-white"
                          onAnimationEnd={() =>
                            goTo((active + 1) % rows.length)
                          }
                        />
                      ) : (
                        <span className="absolute inset-0 bg-neutral-900 md:bg-white" />
                      )
                    ) : null}
                  </span>
                </button>
              )
            })}
          </div>
          <button
            type="button"
            aria-label={playing ? FAMILIAR.pause : FAMILIAR.play}
            onClick={() => setMode(playing ? "paused" : "playing")}
            className="flex size-10 items-center justify-center rounded-full bg-neutral-200/70 text-neutral-900 transition-colors hover:bg-neutral-200 focus-visible:ring-2 focus-visible:ring-neutral-900 focus-visible:outline-none md:pointer-events-auto md:bg-black/35 md:text-white md:backdrop-blur-md md:hover:bg-black/50 md:focus-visible:ring-white"
          >
            {playing ? <PauseIcon /> : <PlayIcon />}
          </button>
        </div>
      </div>
    </section>
  )
}
